import React from "react";
import type { ItemType } from "@/components/DynamicForm";
import type { PersonnelInfo } from "../types";
import formStyles from "@/components/DynamicForm/index.module.less";
import { getCurrentDate } from "@/utils/common";
import dayjs from "dayjs";
import FileFormField from "@/pages/supplier/SupplierException/components/FileFormField";
import SolutionFileDisplay from "@/pages/supplier/SupplierException/SupplierRiskLevel/components/RegisterRiskModal/components/SolutionFileDisplay";
import type { TFunction } from "i18next";

// ==================== 类型定义 ====================
export type PersonnelFieldKey =
  | "purchaseUserNickname"
  | "purchaseUserUsername"
  | "createTime"
  | "purchaseUserDeptName"
  | "purchaseUserPostName"
  | "purchaseUserEmail";

// ==================== 常量配置 ====================
const PERSONNEL_FIELDS: Record<
  PersonnelFieldKey,
  { labelKey: string; placeholderKey: string; disabled: boolean }
> = {
  purchaseUserNickname: {
    labelKey: "员工姓名",
    placeholderKey: "请输入员工姓名",
    disabled: true,
  },
  purchaseUserUsername: {
    labelKey: "员工工号",
    placeholderKey: "请输入员工工号",
    disabled: true,
  },
  createTime: {
    labelKey: "创建日期",
    placeholderKey: "请输入创建日期",
    disabled: true,
  },
  purchaseUserDeptName: {
    labelKey: "部门",
    placeholderKey: "请输入部门",
    disabled: true,
  },
  purchaseUserPostName: {
    labelKey: "岗位",
    placeholderKey: "请输入岗位",
    disabled: true,
  },
  purchaseUserEmail: {
    labelKey: "邮箱",
    placeholderKey: "请输入邮箱",
    disabled: true,
  },
};

const SUPPLIER_TYPE_OPTIONS = [
  { label: "代理商", value: "代理商" },
  { label: "生产商", value: "生产商" },
];

const WAREHOUSE_FREEZE_OPTIONS = [
  { label: "是", value: "是" },
  { label: "否", value: "否" },
];

const SUPPLIER_OPINION_OPTIONS = [
  { label: "退款", value: "退款" },
  { label: "换货", value: "换货" },
];

const REQUIRED_INVESTIGATION_OPTIONS = [
  { label: "是", value: "是" },
  { label: "否", value: "否" },
];

export const FACTORY_OPTIONS = [
  { label: "E1 Bright Future", value: "1000" },
  { label: "E2 BF Chinese Medicine", value: "2000" },
  { label: "E4 SumaUS", value: "4000" },
  { label: "E5 SumaHK", value: "3000" },
];

// ==================== 配置函数 ====================
export const getPersonnelFormItems = (
  _personnelInfo?: PersonnelInfo | null,
  onMaterialCodeSearch?: (
    value: string,
    callback: (options: any[]) => void,
  ) => void,
  _onMaterialCodeChange?: (value: string) => void,
  onWarehouseEntryNumberSearch?: (
    value: string,
    callback: (options: any[]) => void,
  ) => void,
  businessStatus?: number,
  t?: TFunction,
  readOnly?: boolean,
): ItemType[] => {
  const tt = t || ((key: string) => key);

  return [
    {
      name: "personnelWrapperStart",
      col: { span: 24 },
      wrapperStart: { className: formStyles.wrapperPadded },
    },
    {
      label: tt(PERSONNEL_FIELDS.purchaseUserNickname.labelKey),
      name: "purchaseUserNickname",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      widget: {
        widgetType: "Input",
        disabled: PERSONNEL_FIELDS.purchaseUserNickname.disabled,
        placeholder: tt(PERSONNEL_FIELDS.purchaseUserNickname.placeholderKey),
      },
    },
    {
      label: tt(PERSONNEL_FIELDS.purchaseUserUsername.labelKey),
      name: "purchaseUserUsername",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      widget: {
        widgetType: "Input",
        disabled: PERSONNEL_FIELDS.purchaseUserUsername.disabled,
        placeholder: tt(PERSONNEL_FIELDS.purchaseUserUsername.placeholderKey),
      },
    },
    {
      label: tt(PERSONNEL_FIELDS.createTime.labelKey),
      name: "createTime",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      widget: {
        widgetType: "Input",
        disabled: PERSONNEL_FIELDS.createTime.disabled,
        placeholder: tt(PERSONNEL_FIELDS.createTime.placeholderKey),
      },
    },
    {
      label: tt(PERSONNEL_FIELDS.purchaseUserDeptName.labelKey),
      name: "purchaseUserDeptName",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      widget: {
        widgetType: "Input",
        disabled: PERSONNEL_FIELDS.purchaseUserDeptName.disabled,
        placeholder: tt(PERSONNEL_FIELDS.purchaseUserDeptName.placeholderKey),
      },
    },
    {
      label: tt(PERSONNEL_FIELDS.purchaseUserPostName.labelKey),
      name: "purchaseUserPostName",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      widget: {
        widgetType: "Input",
        disabled: PERSONNEL_FIELDS.purchaseUserPostName.disabled,
        placeholder: tt(PERSONNEL_FIELDS.purchaseUserPostName.placeholderKey),
      },
    },
    {
      label: tt(PERSONNEL_FIELDS.purchaseUserEmail.labelKey),
      name: "purchaseUserEmail",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      widget: {
        placeholder: tt(PERSONNEL_FIELDS.purchaseUserEmail.placeholderKey),
        widgetType: "Input",
      },
    },
    {
      name: "purchaserUserId",
      col: { span: 0 },
      widget: {
        widgetType: "Input",
        hidden: true,
      },
    },
    {
      name: "purchaseUserDeptNo",
      col: { span: 0 },
      widget: {
        widgetType: "Input",
        hidden: true,
      },
    },
    {
      name: "purchaseUserPostNo",
      col: { span: 0 },
      widget: {
        widgetType: "Input",
        hidden: true,
      },
    },
    {
      name: "personnelWrapperEnd",
      col: { span: 24 },
      wrapperEnd: true,
    },
    {
      name: "exceptionMaterialSection",
      col: { span: 24 },
      custom: {
        render: () => (
          <div className={formStyles.sectionHeader}>
            <div className={formStyles.sectionIndicator} />
            <span className={formStyles.sectionTitle}>
              {tt("异常物料信息")}
            </span>
          </div>
        ),
      },
    },
    {
      name: "supplierWrapperStart",
      col: { span: 24 },
      wrapperStart: { className: formStyles.wrapperLight },
    },
    {
      label: tt("工厂"),
      name: "factoryValue",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      widget: {
        widgetType: "Select",
        placeholder: tt("请选择工厂"),
        allowClear: true,
        showSearch: true,
        filterOption: false,
        options: FACTORY_OPTIONS,
      },
    },
    {
      label: tt("物料编号"),
      name: "materialCode",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      widget: {
        widgetType: "Select",
        placeholder: tt("请输入物料编号搜索"),
        allowClear: true,
        showSearch: true,
        filterOption: false,
        options: [],
        onSearch: onMaterialCodeSearch,
      },
    },
    {
      label: tt("物料名称"),
      name: "materialName",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      widget: {
        widgetType: "Input",
        disabled: readOnly,
        placeholder: tt("由物料编号自动填写"),
      },
    },
    {
      label: tt("物料进仓批号"),
      name: "warehouseEntryNumber",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      widget: {
        widgetType: "Select",
        placeholder: tt("请输入搜索物料编号"),
        allowClear: true,
        showSearch: true,
        filterOption: false,
        options: [],
        onSearch: onWarehouseEntryNumberSearch,
      },
    },
    {
      label: tt("供应商编号"),
      name: "supplierUserCode",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      widget: {
        widgetType: "Input",
        disabled: readOnly,
        placeholder: tt("由物料编号自动填写"),
      },
    },
    {
      label: tt("供应商名称（中文）"),
      name: "supplierUserName",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      widget: {
        widgetType: "Input",
        disabled: readOnly,
        placeholder: tt("由物料编号自动填写"),
      },
    },
    {
      label: tt("供应商名称（英文）"),
      name: "supplierUsUserName",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      widget: {
        widgetType: "Input",
        placeholder: tt("由物料编号自动填写"),
        disabled: readOnly,
      },
    },
    {
      name: "supplierUserId",
      col: { span: 0 },
      widget: {
        widgetType: "Input",
        hidden: true,
      },
    },
    {
      label: tt("物料类别"),
      name: "materialCategoryValue",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      widget: {
        widgetType: "Select",
        disabled: readOnly,
        placeholder: tt("由物料编号自动填写"),
        options: [],
      },
    },
    {
      label: tt("二级物料类别"),
      name: "materialTwoCategoryValue",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      widget: {
        widgetType: "Select",
        disabled: readOnly,
        placeholder: tt("由物料编号自动填写"),
        options: [],
      },
    },
    {
      label: tt("企业类型"),
      name: "supplierType",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      widget: {
        widgetType: "Radio.Group",
        options: SUPPLIER_TYPE_OPTIONS,
      },
    },
    {
      label: tt("相关生产商名称"),
      name: "manufacturerName",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      widget: {
        widgetType: "Select",
        disabled: readOnly,
        placeholder: tt("由物料编号自动填写"),
        options: [],
      },
    },
    {
      label: tt("生产批号"),
      name: "productionBatch",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      widget: {
        widgetType: "Input",
        disabled: readOnly,
        placeholder: tt("由物料进仓批号自动带出"),
      },
    },
    {
      label: tt("PO单号"),
      name: "poNumber",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      widget: {
        widgetType: "Input",
        disabled: readOnly,
        placeholder: tt("由物料进仓批号自动带出"),
      },
    },
    {
      label: tt("发现异常日期"),
      name: "discoveryDate",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      initialValue: dayjs(getCurrentDate()),
      widget: {
        widgetType: "DatePicker",
        placeholder: tt("请选择发现日期"),
        format: "YYYY-MM-DD",
        style: { width: "100%" },
      },
    },
    {
      label: tt("涉及异常数量"),
      name: "involvedQuantity",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      widget: {
        placeholder: tt("请输入搜索物料编号"),
        widgetType: "Input",
      },
    },
    {
      label: tt("是否回仓冻结"),
      name: "warehouseFreezeValue",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      widget: {
        widgetType: "Select",
        placeholder: tt("请选择通用"),
        options: WAREHOUSE_FREEZE_OPTIONS,
      },
    },
    {
      label: tt("回仓或冻结数量"),
      name: "freezeQuantity",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      isShow: (formValue: any) => formValue?.warehouseFreezeValue === "是",
      widget: {
        widgetType: "Input",
        placeholder: tt("请输入回仓或冻结数量"),
      },
    },
    {
      label: tt("额外工时涉及赔付"),
      name: "extraHours",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      widget: {
        widgetType: "Input",
        placeholder: tt("请输入搜索物料编号"),
      },
    },
    {
      label: tt("问题描述"),
      name: "issueSummary",
      col: { span: 24 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      rules: [{ required: true, message: tt("请输入问题描述") }],
      widget: {
        widgetType: "Input.TextArea",
        placeholder: tt("请输入搜索物料编号"),
        rows: 3,
        style: { resize: "none" },
      },
    },
    {
      label: tt("供应商标签照片"),
      name: "supplierLabelFileIds",
      col: { span: 24 },
      labelCol: { span: 24 },
      wrapperCol: { span: 24 },
      rules: [{ required: true, message: tt("请上传供应商标签照片") }],
      custom: {
        render: (
          _fieldName: any,
          _remove: any,
          fieldValue: any,
          { formRef: _formRef }: any,
        ) => (
          <FileFormField
            fieldName="supplierLabelFileIds"
            initialFiles={fieldValue || []}
            required={true}
            readOnly={readOnly}
            fileType={6}
          />
        ),
      },
    },
    {
      label: tt("相关照片或证明文件"),
      name: "relatedProofFileIds",
      col: { span: 24 },
      labelCol: { span: 24 },
      wrapperCol: { span: 24 },
      rules: [{ required: true, message: tt("请上传相关照片或证明文件") }],
      custom: {
        render: (
          _fieldName: any,
          _remove: any,
          fieldValue: any,
          { formRef: _formRef }: any,
        ) => (
          <FileFormField
            fieldName="relatedProofFileIds"
            initialFiles={fieldValue || []}
            required={true}
            readOnly={readOnly}
            fileType={7}
          />
        ),
      },
    },
    {
      name: "supplierWrapperEnd",
      col: { span: 24 },
      wrapperEnd: true,
    },
    ...(businessStatus === 1 ||
    businessStatus === 2 ||
    businessStatus === 3 ||
    businessStatus === 5
      ? [
          {
            label: tt("决定退款换货"),
            name: "supplierOpinion",
            col: { span: 24 },
            labelCol: { span: 24 },
            wrapperCol: { span: 20 },
            rules: [{ required: true, message: tt("请选择决定退款换货") }],
            widget: {
              widgetType: "Radio.Group",
              options: SUPPLIER_OPINION_OPTIONS,
              disabled: businessStatus === 3 || businessStatus === 5,
            },
          },
        ]
      : []),
    ...(businessStatus === 2 || businessStatus === 3 || businessStatus === 5
      ? [
          {
            name: "qualityClaimSection",
            col: { span: 24 },
            custom: {
              render: () => (
                <div className={formStyles.subSectionTitle}>
                  {tt("质量诉求")}
                </div>
              ),
            },
          },
          {
            label: tt("是否需要调查反馈"),
            name: "requiredInvestigationValue",
            col: { span: 8 },
            labelCol: { span: 24 },
            wrapperCol: { span: 20 },
            widget: {
              widgetType: "Select",
              placeholder: tt("请选择通用"),
              disabled: businessStatus === 3 || businessStatus === 5,
              options: REQUIRED_INVESTIGATION_OPTIONS,
            },
          },
          {
            name: "requiredInvestigationName",
            col: { span: 0 },
            widget: { widgetType: "Input", hidden: true },
          },
          {
            label: tt("期望解决日期"),
            name: "expectedResolutionDate",
            col: { span: 8 },
            labelCol: { span: 24 },
            wrapperCol: { span: 20 },
            widget: {
              widgetType: "DatePicker",
              placeholder: tt("请选择期望解决日期"),
              format: "YYYY-MM-DD",
              disabled: businessStatus === 3 || businessStatus === 5,
              style: { width: "100%" },
            },
          },
          {
            label: tt("诉求说明"),
            name: "description",
            col: { span: 24 },
            labelCol: { span: 24 },
            wrapperCol: { span: 20 },
            widget: {
              widgetType: "Input.TextArea",
              placeholder: tt("请输入搜索物料编号"),
              rows: 3,
              disabled: businessStatus === 3 || businessStatus === 5,
              style: { resize: "none" },
            },
          },
        ]
      : []),
    ...(businessStatus === 5
      ? [
          {
            name: "solutionSection",
            col: { span: 24 },
            custom: {
              render: () => (
                <div className={formStyles.subSectionTitle}>
                  {tt("供应商解决方案")}
                </div>
              ),
            },
          },
          {
            name: "solutionWrapperStart",
            col: { span: 24 },
            wrapperStart: { className: formStyles.wrapperLight },
          },
          {
            name: "srmSupplierFlowBusinessFiles",
            col: { span: 0 },
            widget: { widgetType: "Input", hidden: true },
          },
          {
            name: "solutionFiles",
            col: { span: 24 },
            labelCol: { span: 24 },
            wrapperCol: { span: 24 },
            custom: {
              render: (
                _fieldName: any,
                _remove: any,
                _fieldValue: any,
                { formRef: formRefVal }: any,
              ) => {
                const formData = formRefVal?.current?.getFieldsValue?.();
                const businessFiles =
                  formData?.srmSupplierFlowBusinessFiles || [];
                return <SolutionFileDisplay files={businessFiles} />;
              },
            },
          },
          {
            label: tt("备注说明"),
            name: "remarks",
            col: { span: 24 },
            labelCol: { span: 24 },
            wrapperCol: { span: 20 },
            widget: {
              widgetType: "Input.TextArea",
              placeholder: tt("请输入备注说明表单"),
              rows: 3,
              disabled: true,
              style: { resize: "none" },
            },
          },
          { name: "solutionWrapperEnd", col: { span: 24 }, wrapperEnd: true },
        ]
      : []),
    ...(businessStatus === 5
      ? [
          {
            name: "approvalSection",
            col: { span: 24 },
            custom: {
              render: () => (
                <div className={formStyles.subSectionTitle}>
                  {tt("审批信息")}
                </div>
              ),
            },
          },
          {
            label: tt("审批意见"),
            name: "approvalOpinion",
            col: { span: 24 },
            labelCol: { span: 3 },
            wrapperCol: { span: 20 },
            className: "inlineLabel",
            widget: {
              widgetType: "Radio.Group",
              options: [
                { label: tt("同意"), value: "agree" },
                { label: tt("驳回"), value: "reject" },
              ],
              disabled: false,
            },
          },
          {
            label: tt("意见说明"),
            name: "approvalComments",
            col: { span: 24 },
            labelCol: { span: 3 },
            wrapperCol: { span: 22 },
            className: "inlineLabel",
            widget: {
              widgetType: "Input.TextArea",
              placeholder: tt("请输入意见说明"),
              rows: 4,
              style: { resize: "none" },
              disabled: false,
            },
          },
        ]
      : []),
  ];
};
